type UnknownError = {
  message?: unknown;
  status?: unknown;
  statusText?: unknown;
  details?: unknown;
};

function asObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? value as Record<string, unknown> : null;
}

function firstText(...values: unknown[]) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

export function getRawErrorMessage(error: unknown) {
  const err = asObject(error) as UnknownError | null;
  const details = asObject(err?.details);
  const nestedError = asObject(details?.error);

  return firstText(
    nestedError?.message,
    details?.message,
    err?.message,
    typeof error === 'string' ? error : ''
  );
}

export function getErrorStatus(error: unknown) {
  const err = asObject(error) as UnknownError | null;
  const details = asObject(err?.details);
  const status = Number(err?.status ?? details?.status ?? details?.statusCode);
  return Number.isFinite(status) ? status : null;
}

export function getErrorCorrelationId(error: unknown) {
  const err = asObject(error) as UnknownError | null;
  const details = asObject(err?.details);
  const nestedError = asObject(details?.error);

  return firstText(
    nestedError?.correlationId,
    details?.correlationId
  );
}

export function formatUserFacingError(
  error: unknown,
  fallbackMessage: string,
  nextAction = 'Try again. If this keeps happening, check the API connection.'
) {
  const rawMessage = getRawErrorMessage(error);
  const status = getErrorStatus(error);
  const correlationId = getErrorCorrelationId(error);
  const supportReference = correlationId ? ` Reference: ${correlationId}.` : '';
  const normalized = rawMessage.toLowerCase();
  const looksTechnicalServerError = (
    (status !== null && status >= 500)
    || /^http\s+5\d\d\b/i.test(rawMessage)
    || normalized.includes('internal server error')
  );

  if (looksTechnicalServerError) {
    return `${fallbackMessage} ${nextAction}${supportReference}`;
  }

  if (status === 401 || status === 403) {
    return 'You do not have access to this action. Sign in again or ask an admin to check your role.';
  }

  if (
    normalized.includes('failed to fetch')
    || normalized.includes('networkerror')
    || normalized.includes('network error')
    || normalized.includes('load failed')
  ) {
    return `${fallbackMessage} PMApp could not reach the API. Check the connection, then try again.`;
  }

  return rawMessage || fallbackMessage;
}
